import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Box, Typography, Card, CardContent, Chip, Divider, IconButton } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';

import { setProjectData, setEditingState, setConcatList } from '@modules/editor';
import VideoSelectionModal from './VideoSelectionModal';
import api from '@utils/axiosInstance';

function ConcatMenu() {

    const { uuid } = useParams();
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const concatList = useSelector((state: any) => state.editor.concatList);
    const [concatModalOpen, setConcatModalOpen] = useState(false);
    const [videoListCount, setVideoListCount] = useState(0);
    const [currentVodList, setCurrentVodList] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [type, setType] = useState('');
    const videoListPerPage = 30;

    const cdn = useSelector((state: any) => state.organization.cdn);
    const organizationData = useSelector((state: any) => state.organization.organizationData);
    const userToken = useSelector((state: any) => state.organization.userToken);
    const projectData = useSelector((state: any) => state.editor.projectData);
    const projectInfo = useSelector((state: any) => state.editor.projectInfo);
    const editProjIdx = useSelector((state: any) => state.editor.editProjIdx);
    const vodInfo = useSelector((state: any) => state.media.vodInfo);

    const [isLoading, setIsLoading] = useState(false);
    const [projectVideos, setProjectVideos] = useState<any[]>([]);
    const [totalDuration, setTotalDuration] = useState(0);

    useEffect(() => {
        setIsLoading(true);

        api({
            method: 'post',
            url: '/vod/getVodList',
            data: {
                userToken: userToken,
                page: currentPage,
                organizationCode: organizationData.organization_code
            }
        })
            .then((response) => {
                if (response.data.statusCode == 100) {
                    setVideoListCount(response.data.vodList.count);
                    setCurrentVodList(response.data.vodList.rows);
                }
            })
            .catch((error) => {
                console.log(error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [currentPage]);

    // 프로젝트 요약 정보 계산을 위한 useEffect
    useEffect(() => {
        const videos = [];
        console.log('concatList', concatList, vodInfo);
        // Before video
        if (concatList.before) {
            videos.push({
                type: 'before',
                realFileName: concatList.before.realFileName,
                uuid: concatList.before.uuid,
                playTime: concatList.before.playTime || 0,
                thumbnail: `${cdn}/${concatList.before.uuid}/thumbnail/tn_1.png`
            });
        }

        // Original video
        if (vodInfo) {
            videos.push({
                type: 'original',
                realFileName: vodInfo.realFileName || '원본 영상',
                uuid: vodInfo.uuid,
                thumbnail: `${cdn}/${vodInfo.uuid}/thumbnail/tn_1.png`,
                playTime: vodInfo.playTime || 0,
                resolution: vodInfo.resolution || 'Unknown'
            });
        }

        // After video
        if (concatList.after) {
            videos.push({
                type: 'after',
                realFileName: concatList.after.realFileName,
                uuid: concatList.after.uuid,
                playTime: concatList.after.playTime || 0,
                thumbnail: `${cdn}/${concatList.after.uuid}/thumbnail/tn_1.png`
            });
        }

        setProjectVideos(videos);
        console.log('videos', videos);
        const total = videos.reduce((total, video) => total + (video.playTime || 0), 0);
        setTotalDuration(total);
    }, [concatList, vodInfo, cdn]);

    function handleSelectedRow(el: any) {
        console.log('eeeee', el);
        var tmpConcatList = { ...concatList };

        setConcatModalOpen(false);
        if (type == 'before') {
            tmpConcatList.before = { ...el };
        }
        else if (type == 'after') {
            tmpConcatList.after = { ...el };
        }
        dispatch(setConcatList(tmpConcatList));
        // concat 변경 시 editingState 업데이트
        dispatch(setEditingState('concat', true));
    }

    function handleClearSelection(position: string) {
        const tmpConcatList = { ...concatList };

        if (position === 'before') {
            delete tmpConcatList.before;
        } else if (position === 'after') {
            delete tmpConcatList.after;
        }
        dispatch(setConcatList(tmpConcatList));
        dispatch(setEditingState('concat', true));
    }

    return (
        <>
            <Box className="concat-menu">
                <Box className="concat-menu__container">
                    {/* Before Position */}
                    <Card className={`concat-menu__card ${concatList.hasOwnProperty('before') ? 'concat-menu__card--selected' : 'concat-menu__card--empty'}`}>
                        <CardContent className="concat-menu__card-content">
                            <Box className="concat-menu__header">
                                <Box className="concat-menu__header-left">
                                    <Chip
                                        label={t('vod.manage.editor_tab.position_before')}
                                        className="concat-menu__chip"
                                    />
                                </Box>
                                {concatList.hasOwnProperty('before') && (
                                    <Box className="concat-menu__header-right">
                                        <IconButton
                                            size="small"
                                            onClick={() => { setConcatModalOpen(true); setType('before') }}
                                            className="concat-menu__icon-button"
                                        >
                                            <RefreshIcon />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleClearSelection('before')}
                                            className="concat-menu__icon-button"
                                        >
                                            <ClearIcon />
                                        </IconButton>
                                    </Box>
                                )}
                            </Box>

                            {concatList.hasOwnProperty('before') ? (
                                <Box className="concat-menu__video-info">
                                    <Typography className="concat-menu__video-title">
                                        {concatList.before?.realFileName}
                                    </Typography>
                                    <Typography className="concat-menu__video-uuid">
                                        {concatList.before?.uuid}
                                    </Typography>
                                </Box>
                            ) : (
                                <Box
                                    onClick={() => { setConcatModalOpen(true); setType('before') }}
                                    className="concat-menu__add-button"
                                >
                                    <AddIcon className="concat-menu__add-icon" />
                                    <Typography className="concat-menu__add-text">
                                        {t('vod.manage.editor_tab.find_video')}
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    {/* After Position */}
                    <Card className={`concat-menu__card ${concatList.hasOwnProperty('after') ? 'concat-menu__card--selected' : 'concat-menu__card--empty'}`}>
                        <CardContent className="concat-menu__card-content">
                            <Box className="concat-menu__header">
                                <Box className="concat-menu__header-left">
                                    <Chip
                                        label={t('vod.manage.editor_tab.position_after')}
                                        className="concat-menu__chip"
                                    />
                                </Box>

                                {concatList.hasOwnProperty('after') && (
                                    <Box className="concat-menu__header-right">
                                        <IconButton
                                            size="small"
                                            onClick={() => { setConcatModalOpen(true); setType('after') }}
                                            className="concat-menu__icon-button"
                                        >
                                            <RefreshIcon />
                                        </IconButton>

                                        <IconButton
                                            size="small"
                                            onClick={() => handleClearSelection('after')}
                                            className="concat-menu__icon-button"
                                        >
                                            <ClearIcon />
                                        </IconButton>
                                    </Box>
                                )}
                            </Box>

                            {concatList.hasOwnProperty('after') ? (
                                <Box className="concat-menu__video-info">
                                    <Typography className="concat-menu__video-title">
                                        {concatList.after?.realFileName}
                                    </Typography>
                                    <Typography className="concat-menu__video-uuid">
                                        {concatList.after?.uuid}
                                    </Typography>
                                </Box>
                            ) : (
                                <Box
                                    onClick={() => { setConcatModalOpen(true); setType('after') }}
                                    className="concat-menu__add-button"
                                >
                                    <AddIcon className="concat-menu__add-icon" />
                                    <Typography className="concat-menu__add-text">
                                        {t('vod.manage.editor_tab.find_video')}
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    {/* Project Summary */}
                    {(concatList.before || concatList.after) && (
                        <Card className="concat-menu__summary-card">
                            <CardContent className="concat-menu__card-content">
                                <Box className="concat-menu__header-left">
                                    <Typography variant="h6" className="concat-menu__summary-title">
                                        프로젝트 요약
                                    </Typography>
                                </Box>

                                <Divider className="concat-menu__divider" />

                                {/* Video Sequence */}
                                <Box className="concat-menu__video-sequence">
                                    <Typography variant="subtitle1" className="concat-menu__section-title">
                                        영상 순서
                                    </Typography>
                                    <Box className="concat-menu__video-grid">
                                        {projectVideos.map((video, index) => (
                                            <Box key={`${video.type}-${video.uuid}-${index}`} className="concat-menu__video-item">
                                                <Box className="concat-menu__video-thumbnail">
                                                    <img
                                                        src={video.thumbnail}
                                                        alt={video.realFileName}
                                                        className="concat-menu__video-thumbnail-img"
                                                    />
                                                    <Box className="concat-menu__video-type-badge">
                                                        {video.type === 'before' ? 'Before' :
                                                            video.type === 'original' ? 'Original' : 'After'}
                                                    </Box>
                                                </Box>
                                                <Typography className="concat-menu__video-name">
                                                    {video.realFileName}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>

                                {/* Project Info */}
                                <Box className="concat-menu__project-info">
                                    <Box className="concat-menu__info-item">
                                        <Typography className="concat-menu__info-label">
                                            총 길이:
                                        </Typography>
                                        <Typography className="concat-menu__info-value">
                                            {dayjs(dayjs.utc(totalDuration * 1000)).format("HH:mm:ss")}
                                        </Typography>
                                    </Box>

                                    {vodInfo?.resolution && (
                                        <Box className="concat-menu__info-item">
                                            <Typography className="concat-menu__info-label">
                                                해상도:
                                            </Typography>
                                            <Typography className="concat-menu__info-value">
                                                {vodInfo.resolution}
                                            </Typography>
                                        </Box>
                                    )}

                                    <Box className="concat-menu__info-item">
                                        <Typography className="concat-menu__info-label">
                                            영상 수:
                                        </Typography>
                                        <Typography className="concat-menu__info-value">
                                            {projectVideos.length}개
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    )}
                </Box>
            </Box>

            <VideoSelectionModal
                open={concatModalOpen}
                onClose={() => setConcatModalOpen(false)}
                currentVodList={currentVodList}
                videoListCount={videoListCount}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                handleSelectedRow={handleSelectedRow}
                isLoading={isLoading}
                cdn={cdn}
            />
        </>
    );
}

export default ConcatMenu;
